/**
 * postTerminalHydrator.js — RC 1.55 P0 post-deploy fix
 * (operator-locked, 2026-05-12 PM)
 *
 * Generic post-terminal hydration helper for Cover Letter + Interview
 * Prep sections. Mirrors `hydrationVerifier.js` (CV) but adds an
 * operator-requested retry-with-backoff so the rare "row committed
 * but not fully readable at the terminal poll resolve moment" race
 * can self-heal without forcing the user to switch tabs.
 *
 * Hard rails (operator-locked):
 *   • READ-ONLY GETs. NO POST. NO LLM dispatch. NO `/api/runs` call.
 *   • Does NOT trigger a new charge. Idempotent w.r.t billing.
 *   • If hydration ultimately fails after the full backoff schedule,
 *     raises `recoverable=true` so the section can surface a calm
 *     "Generated and saved. Refreshing preview…" banner + retry
 *     button that simply re-invokes this helper (no Generate).
 *   • Backoff schedule operator-locked: [750ms, 1500ms, 3000ms].
 *     First attempt is immediate (no sleep). Total worst-case wait
 *     ≈ 5.25 s, well inside the controller's `stabilizing` window.
 *
 * Why this exists:
 *   Production smoke showed Cover Letter + Interview Prep sections
 *   rendering empty after a successful generation, even though the
 *   server-side row was persisted and charged. Operator confirmed
 *   the artefact was already in DB (clicking Generate again pulled
 *   it out as an idempotent replay). Root cause: both sections bind
 *   UI state directly from the dispatch envelope `r`, which can be
 *   thin in the polling-helper terminal-poll race window. This
 *   helper closes that gap by performing an authoritative canonical
 *   GET with retry after dispatch returns.
 */

import { trace } from './actionTrace.js';

const DEFAULT_BACKOFF_MS = [750, 1500, 3000];

function _sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * hydrateWithRetry — generic post-terminal canonical GET with retry.
 *
 * @param {object} args
 * @param {string} args.action              Logging key (e.g. 'cover_letter').
 * @param {string} args.path                Canonical GET path (e.g. '/api/cover-letters/<id>').
 * @param {object} [args.headers]           Optional GET headers (e.g. canonical-envelope opt-in).
 * @param {(p:string, o:object)=>Promise<object>} args.apiGet
 * @param {(row:object)=>boolean}           args.isComplete   Predicate: row is ready to bind.
 * @param {object}                          [args.envelope]   The thin terminal envelope, if any.
 * @param {(env:object)=>boolean}           [args.envelopeIsFresh]
 *   Predicate over the dispatch envelope — if it already has full
 *   content, short-circuit and skip the GETs entirely.
 * @param {number[]}                        [args.backoffMs]  Override schedule. Default [750,1500,3000].
 * @param {string}                          [args.logId]      Correlation id for trace logs.
 * @returns {Promise<object>}               The canonical row (or the
 *                                          original envelope when it
 *                                          was already fresh).
 * @throws  {Error} with `recoverable=true` when the row is never
 *                  readable within the full backoff schedule.
 */
export async function hydrateWithRetry(args) {
  const {
    action,
    path,
    headers,
    apiGet,
    isComplete,
    envelope,
    envelopeIsFresh,
    backoffMs = DEFAULT_BACKOFF_MS,
    logId,
  } = args;

  if (!apiGet || typeof apiGet !== 'function') {
    throw new Error('hydrateWithRetry: apiGet is required');
  }
  if (!path) {
    const err = new Error('hydrateWithRetry: canonical GET path is required');
    err.code = `${action}_hydrate_missing_path`;
    throw err;
  }
  if (typeof isComplete !== 'function') {
    throw new Error('hydrateWithRetry: isComplete predicate is required');
  }

  // ── 1. Happy path: dispatch envelope already carries full content. ──
  if (envelope && typeof envelopeIsFresh === 'function' && envelopeIsFresh(envelope)) {
    trace(action, 'post_terminal_envelope_fresh', { logId });
    return envelope;
  }

  // ── 2. Canonical GET with retry. ──
  // First attempt is immediate (no sleep). Subsequent attempts honour
  // the operator-locked backoff schedule.
  const attempts = [0, ...backoffMs];
  let lastRow = null;
  let lastErr = null;
  for (let i = 0; i < attempts.length; i += 1) {
    if (attempts[i] > 0) {
      // eslint-disable-next-line no-await-in-loop
      await _sleep(attempts[i]);
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      const row = await apiGet(path, headers ? { headers } : undefined);
      if (row && typeof row === 'object') {
        lastRow = row;
        if (isComplete(row)) {
          trace(action, 'post_terminal_hydrate_ok', {
            logId, attempt: i, totalWaitMs: attempts.slice(0, i + 1).reduce((a, b) => a + b, 0),
          });
          return row;
        }
        trace(action, 'post_terminal_hydrate_incomplete_retry', {
          logId, attempt: i, run_status: row.run_status || row.status,
        });
      }
    } catch (e) {
      lastErr = e;
      trace(action, 'post_terminal_hydrate_read_error_retry', {
        logId, attempt: i, error: e?.message,
      });
    }
  }

  // ── 3. Schedule exhausted. Raise a recoverable error. ──
  const err = new Error(`${action}: artefact saved but preview is still refreshing.`);
  err.code = `${action}_hydrate_exhausted`;
  err.recoverable = true;
  err.lastRow = lastRow;
  err.lastReadError = lastErr;
  trace(action, 'post_terminal_hydrate_exhausted', {
    logId,
    last_status: lastRow ? (lastRow.run_status || lastRow.status) : null,
  });
  throw err;
}

// ──────────────────────────────────────────────────────────────────
// Cover Letter — predicate + thin wrapper.
// ──────────────────────────────────────────────────────────────────

/**
 * Cover letter row from `GET /api/cover-letters/{id}` is "complete"
 * when it carries non-empty `content_markdown` (or back-compat
 * `generated_text` on legacy rows). The row's `id` MUST match the
 * requested letter id.
 */
export function coverLetterRowIsComplete(row) {
  if (!row || typeof row !== 'object') return false;
  // Some endpoints surface a polling envelope with status field —
  // accept either "complete" or absence (raw row).
  const status = (row.status || row.run_status || 'complete').toString().toLowerCase();
  if (status !== 'complete') return false;
  const content = row.content_markdown || row.generated_text || '';
  return typeof content === 'string' && content.length > 0;
}

export function coverLetterEnvelopeIsFresh(env) {
  if (!env || typeof env !== 'object') return false;
  if (!env.cover_letter_id && !env.id) return false;
  const content = env.generated_text || env.content_markdown || '';
  return typeof content === 'string' && content.length > 0;
}

/**
 * Convenience wrapper.
 *
 * @param {object} args
 * @param {object} args.envelope    Dispatch terminal envelope.
 * @param {string} args.letterId    Canonical id (preferred over envelope's).
 * @param {Function} args.apiGet    apiFetch.
 * @param {string} [args.logId]
 */
export function verifyAndHydrateCoverLetter({ envelope, letterId, apiGet, logId }) {
  if (!letterId) {
    return Promise.reject(Object.assign(
      new Error('verifyAndHydrateCoverLetter: letterId required'),
      { code: 'cover_letter_id_missing', recoverable: false },
    ));
  }
  return hydrateWithRetry({
    action:           'cover_letter',
    path:             `/api/cover-letters/${letterId}`,
    apiGet,
    envelope,
    envelopeIsFresh:  coverLetterEnvelopeIsFresh,
    isComplete:       coverLetterRowIsComplete,
    logId:            logId || letterId,
  });
}

// ──────────────────────────────────────────────────────────────────
// Interview Prep — predicate + thin wrapper.
// ──────────────────────────────────────────────────────────────────

/**
 * Interview prep row from `GET /api/interview-prep/{id}` with the
 * `X-Envelope: rc_1_52_canonical` header returns:
 *   { status:'complete', result:{ interview_prep:{...}, score, ... }, billing, ... }
 *
 * Row is "complete" when status is 'complete' AND result.interview_prep
 * is a non-empty object.
 */
export function interviewPrepRowIsComplete(row) {
  if (!row || typeof row !== 'object') return false;
  const status = (row.status || row.run_status || '').toString().toLowerCase();
  if (status && status !== 'complete') return false;
  const res = row.result || row;
  const ip = res?.interview_prep;
  if (!ip || typeof ip !== 'object') return false;
  // The canonical interview_prep block carries at least one of:
  //   technical_questions, behavioural_questions, situational_questions,
  //   followup_questions, prep_strategy, opening_pitch, ...
  // We accept any non-empty top-level field as "complete".
  const keys = Object.keys(ip);
  if (keys.length === 0) return false;
  // Defensive: ensure at least one populated array/object/string.
  return keys.some((k) => {
    const v = ip[k];
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'string') return v.length > 0;
    if (v && typeof v === 'object') return Object.keys(v).length > 0;
    return false;
  });
}

export function interviewPrepEnvelopeIsFresh(env) {
  if (!env || typeof env !== 'object') return false;
  if (!env.interview_prep_id && !env.run_id && !env.id) return false;
  // The dispatch envelope flattens result into `interview_prep` at top level.
  return !!(env.interview_prep && typeof env.interview_prep === 'object'
    && Object.keys(env.interview_prep).length > 0);
}

export function verifyAndHydrateInterviewPrep({ envelope, ipId, apiGet, logId }) {
  if (!ipId) {
    return Promise.reject(Object.assign(
      new Error('verifyAndHydrateInterviewPrep: ipId required'),
      { code: 'interview_prep_id_missing', recoverable: false },
    ));
  }
  return hydrateWithRetry({
    action:           'interview_prep',
    path:             `/api/interview-prep/${ipId}`,
    headers:          { 'X-Envelope': 'rc_1_52_canonical' },
    apiGet,
    envelope,
    envelopeIsFresh:  interviewPrepEnvelopeIsFresh,
    isComplete:       interviewPrepRowIsComplete,
    logId:            logId || ipId,
  });
}
